import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  ReactNode,
  useImperativeHandle,
} from 'react';

import { AutocompleteMultipleCheckbox } from 'components';
import { IOption } from 'components/atoms/AutocompleteMultipleCheckbox/types';
import { IFilterBarItem } from './types';

const FilterBarItem = forwardRef<ReactNode, IFilterBarItem>(
  (
    {
      id,
      label,
      name,
      options,
      loading,
      onFilterChange,
      fetchNextPage,
      hasNextPage,
      setInputFieldValue,
      initialState = [],
      triggerOnChange,
      selectSearchTerm,
    },
    ref,
  ) => {
    const [selectedOptions, setSelectedOptions] =
      useState<IOption[]>(initialState);

    // equivale aos valores já selecionados dentro das opções
    const selectOnlyAvaliableOptions = selectedOptions.filter(sItem =>
      options.some(fItem => fItem.value === sItem.value),
    );

    const clearFilter = () => {
      setSelectedOptions(initialState);
    };

    useImperativeHandle(ref, () => ({
      clearFilter,
    }));

    useEffect(() => {
      /**
       * atualiza a lista de valores selecionados
       * com base no resultado filtro nas opções selecionadas
       * dentro das opções disponíveis.
       */
      if (
        options.length > 0 &&
        selectedOptions.length !== selectOnlyAvaliableOptions.length
      ) {
        setSelectedOptions(selectOnlyAvaliableOptions);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    useEffect(() => {
      /**
       * Toda alteração dos valores selecionados
       * devem ser refletidas no onFilterChande.
       */
      onFilterChange({ name, value: selectedOptions });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions]);

    const getOptionLabel = (option: IOption) => `${option.label}`;

    const handleToggleOption = useCallback(
      (toggleOptions: Array<IOption>) => {
        if (triggerOnChange) triggerOnChange();
        setSelectedOptions(toggleOptions);
      },
      [triggerOnChange],
    );

    const handleClearOptions = useCallback(
      () => handleToggleOption([]),
      [handleToggleOption],
    );

    const handleSelectAll = useCallback(
      (isSelected: boolean) => {
        if (isSelected) {
          handleToggleOption(options);
        } else {
          handleClearOptions();
        }
      },
      [handleToggleOption, handleClearOptions, options],
    );

    const hasMoreOneSelected = selectedOptions.length > 1;

    const getLimitTagsText = (more: number) =>
      `${more} ${hasMoreOneSelected ? 'Itens' : 'Item'}`;

    return (
      <AutocompleteMultipleCheckbox
        selectAllLabel="Selecionar todos"
        noOptionsText="Nenhum resultado encontrado"
        id={id}
        label={label}
        items={options}
        limitTags={0}
        disableClearable
        loading={loading}
        getOptionLabel={getOptionLabel}
        getLimitTagsText={getLimitTagsText}
        selectedValues={selectOnlyAvaliableOptions}
        onToggleOption={handleToggleOption}
        onClearOptions={handleClearOptions}
        onSelectAll={handleSelectAll}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        setInputFieldValue={setInputFieldValue}
        selectSearchTerm={selectSearchTerm}
      />
    );
  },
);

export default FilterBarItem;
